<template>
  <el-form
    v-if="model"
    class="inscription-form"
    :class="{
      'inscription-form--collapsed': isCollapsed,
      'inscription-form--first': isFirst,
      'inscription-form--last': isLast,
    }"
    :rules="rules"
    :model="model"
    :validate-on-rule-change="false"
    ref="form"
    @click.native="onInscriptionClick"
    @validate="validate"
    @submit.prevent.native="validate"
    @keypress.enter.native="validate"
  >
    <!-- Uncollapsed Inscription -->
    <div
      v-show="!isCollapsed"
      class="inscription-form__state--uncollapsed"
    >
      <el-row class="inscription-form__state--uncollapsed__header">
        <el-col :lg="24" :md="24" :sm="24">
          <i class="inscription-form__state--uncollapsed__icon icon-player" />
          <span v-text="playerName" />
          <i
            v-if="removable"
            class="inscription-form__state--uncollapsed__header__icon icon-cross"
            @click.stop.prevent="onDelete"
          />
        </el-col>
      </el-row>
      <el-row
        :gutter="10"
        class="inscription-form__state--uncollapsed__content"
      >
        <!-- First Name -->
        <el-col :lg="10" :md="10" :sm="10">
          <el-form-item
            :label="fields.firstName.label"
            :prop="fields.firstName.name"
            :required="fields.firstName.required"
          >
            <el-input
              v-model="model[fields.firstName.name]"
              :placeholder="i18n('entities.inscription.placeholders.firstName')"
              prefix-icon="icon-player"
            />
          </el-form-item>
        </el-col>

        <!-- Last Name -->
        <el-col :lg="14" :md="14" :sm="14">
          <el-form-item
            :label="fields.lastName.label"
            :prop="fields.lastName.name"
            :required="fields.lastName.required"
          >
            <el-input
              v-model="model[fields.lastName.name]"
              :placeholder="i18n('entities.inscription.placeholders.lastName')"
              prefix-icon="icon-player"
            />
          </el-form-item>
        </el-col>

        <!-- Gender -->
        <el-col :lg="5" :md="5" :sm="5">
          <el-form-item
            :label="fields.gender.label"
            :prop="fields.gender.name"
            :required="fields.gender.required"
          >
            <el-select
              :placeholder="i18n('entities.inscription.placeholders.gender')"
              v-model="model[fields.gender.name]"
              @change="doMatch"
            >
              <i
                slot="prefix"
                class="icon-family"
              />
              <el-option
                :key="option.id"
                :label="option.label"
                :value="option.id"
                v-for="option in fields.gender.options"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <!-- Birthdate -->
        <el-col :lg="5" :md="5" :sm="5">
          <el-form-item
            :label="fields.birthdate.label"
            :prop="fields.birthdate.name"
            :required="fields.birthdate.required"
          >
            <el-date-picker
              type="date"
              :placeholder="i18n('common.date.formatInput')"
              :format="i18n('common.date.formatInput')"
              v-model="model[fields.birthdate.name]"
              @change="doMatch"
            >
            </el-date-picker>
          </el-form-item>
        </el-col>

        <!-- Is Member? -->
        <el-col
          :lg="13" :md="13" :sm="13" :offset="1"
          class="inscription-form__is-member"
        >
          <el-form-item
            class="el-form-item--radio"
            :prop="fields.isMember.name"
            required
          >
            <label
              slot="label"
              :for="fields.additionalDocFiles.name"
              v-html="'&nbsp;'"
            />
            <span v-text="fields.isMember.label" />
            <el-radio-group v-model="model[fields.isMember.name]">
              <el-radio :label="true">
                <app-i18n code="common.yes" />
              </el-radio>
              <el-radio :label="false">
                <app-i18n code="common.no" />
              </el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>

        <!-- Matching Player w/ Subprogram -->
        <el-col :lg="24" :md="24" :sm="24">
          <app-i18n
            v-if="subprogramMatchStatus === 'pending'"
            class="inscription-form__matching inscription-form__matching--pending"
            code="entities.inscription.matching.default"
          />
          <p
            v-else-if="subprogramMatchStatus === 'loading'"
            class="inscription-form__matching inscription-form__matching--loading"
          >
            <app-i18n code="entities.inscription.matching.loading" />
            <loading-spinner />
          </p>
          <app-i18n
            v-else-if="subprogramMatchStatus === 'error'"
            class="inscription-form__matching inscription-form__matching--error"
            code="entities.inscription.matching.error"
          />
          <p
            v-else-if="subprogramMatchStatus === 'success'"
            class="inscription-form__matching inscription-form__matching--success"
            :class="{ 'inscription-form__matching inscription-form__matching--success--end': !subprogramMatchHasExtraFields}"
          >
            <app-i18n code="entities.inscription.matching.success" />
            <span v-text="' '" />
            <strong v-text="subprogramMatch.category.name" />
          </p>
        </el-col>

        <!-- Sport career -->
        <el-col
          v-if="!model[fields.isMember.name]"
          :lg="24" :md="24" :sm="24"
        >
          <el-form-item
            :label="fields.careerInfo.label"
            :prop="fields.careerInfo.name"
            :required="fields.careerInfo.required"
          >
            <el-textarea
              v-model="model[fields.careerInfo.name]"
              :placeholder="i18n('entities.inscription.placeholders.careerInfo')"
              :min-rows="3"
              :max-rows="6"
              prefix-icon="icon-document"
            />
          </el-form-item>
        </el-col>

        <!-- Subprogram specific fields -->
        <el-col :lg="24" :md="24" :sm="24"
          v-if="subprogramMatchHasExtraFields"
          class="inscription-form__specific"
        >
          <!-- ID / Passport -->
          <el-row
            v-if="subprogramMatch && subprogramMatch.identDocNeeded"
            :gutter="10"
          >
            <el-col :lg="5" :md="5" :sm="5">
              <el-form-item
                :label="fields.identDoc.label"
                :prop="fields.identDoc.name"
                :required="fields.identDoc.required"
              >
                <el-input
                  v-model="model[fields.identDoc.name]"
                  :placeholder="i18n('entities.inscription.placeholders.identDoc')"
                  prefix-icon="icon-document"
                />
              </el-form-item>
            </el-col>
            <el-col :lg="19" :md="19" :sm="19">
              <el-form-item
                class="el-form-item--upload"
                :prop="fields.identDocFiles.name"
                :required="fields.identDocFiles.required"
              >
                <label
                  slot="label"
                  :for="fields.additionalDocFiles.name"
                  v-html="'&nbsp;'"
                />
                <app-file-upload
                  ref="identDocFiles"
                  multiple
                  :accept="fields.identDocFiles.formats"
                  :rows="4"
                  :auto-upload="false"
                  :max="fields.identDocFiles.max"
                  :path="fields.identDocFiles.path(model.id, currentUser.id)"
                  :placeholder="fields.identDocFiles.label"
                  :schema="fields.identDocFiles.fileSchema"
                  v-model="model[fields.identDocFiles.name]"
                  @input="$refs.form.validateField(fields.identDocFiles.name)"
                />
              </el-form-item>
            </el-col>
          </el-row>

          <!-- Additional docs -->
          <el-row
            v-if="subprogramMatch.additionalDoc && subprogramMatch.additionalDoc.exist"
            :gutter="10"
          >
            <el-col :lg="24" :md="24" :sm="24">
              <el-form-item class="inscription-form__specific__additional-docs">
                <div slot="label">
                  <label
                    class="inscription-form__specific__additional-docs__label"
                    v-text="fields.additionalDocFiles.label"
                  />
                </div>
                <p
                  v-if="subprogramMatch.additionalDoc.text && subprogramMatch.additionalDoc.text.length"
                  class="inscription-form__specific__additional-docs__text"
                  v-text="subprogramMatch.additionalDoc.text"
                />
                <p
                  v-else
                  class="inscription-form__specific__additional-docs__text"
                  v-text="i18n('entities.inscription.placeholders.additionalDocText')"
                />
              </el-form-item>
            </el-col>
            <el-col
              v-if="subprogramMatch.additionalDoc.requiredDocs && subprogramMatch.additionalDoc.requiredDocs.length"
              :lg="24" :md="24" :sm="24"
            >
              <div
                v-for="(doc, index) in subprogramMatch.additionalDoc.requiredDocs"
                class="inscription-form__specific__required-docs"
                :key="`inscription-form__specific__required-docs--${doc.title}`"
              >
                <a
                  :ref="`downloadLink--${index}`"
                  class="inscription-form__specific__required-docs__link"
                  :href="doc.file.publicUrl"
                  :download="doc.title"
                  target="_blank"
                  @click="doDownload($event, index, doc.file, doc.title)"
                >
                  <span
                    class="inscription-form__specific__required-docs__title el-link el-link--default"
                    v-text="doc.title"
                  />
                  <el-button class="inscription-form__specific__required-docs__button">
                    <div class="inscription-form__specific__required-docs__button__content">
                      <i class="icon-download inscription-form__specific__required-docs__button__content__icon" />
                      <span v-text="i18n('common.download')"/>
                    </div>
                  </el-button>
                </a>
              </div>
            </el-col>
            <el-col
              class="inscription-form__specific__additional-docs-upload"
              :lg="24" :md="24" :sm="24"
            >
              <el-form-item
                class="el-form-item--upload"
                :prop="fields.additionalDocFiles.name"
                :required="fields.additionalDocFiles.required"
              >
                <label
                  slot="label"
                  :for="fields.additionalDocFiles.name"
                  v-html="'&nbsp;'"
                />
                <app-file-upload
                  ref="additionalDocFiles"
                  multiple
                  :auto-upload="false"
                  :max="fields.additionalDocFiles.max"
                  :path="fields.additionalDocFiles.path(model.id, currentUser.id)"
                  :placeholder="fields.additionalDocFiles.label"
                  :schema="fields.additionalDocFiles.fileSchema"
                  v-model="model[fields.additionalDocFiles.name]"
                  @input="$refs.form.validateField(fields.additionalDocFiles.name)"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>

        <!-- Global error message -->
        <el-col :lg="24" :md="24" :sm="24">
          <el-form-item
            v-if="globalErrorMessage"
            :error="globalErrorMessage"
          />
        </el-col>

        <!-- Form controls -->
        <el-col :lg="24" :md="24" :sm="24">
          <div class="inscription-form__buttons form-buttons form-buttons--sides">
            <el-button
              type="primary"
              @click.prevent.stop="doSave"
            >
              <app-i18n code="common.save" />
            </el-button>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- Collapsed Inscription -->
    <div
      v-show="isCollapsed"
      class="inscription-form__state--collapsed"
      :class="{ 'inscription-form__state--error': hasErrors || subprogramMatchStatus !== 'success' }"
    >
      <i
        class="inscription-form__state--collapsed__icon"
        :class="collapsedIcon"
      />
      <span
        class="inscription-form__state--collapsed__label"
        v-text="playerName"
      />
      <i
        v-if="removable"
        class="inscription-form__state--collapsed__delete icon-cross"
        @click.stop.prevent="onDelete"
      />
    </div>
  </el-form>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import AsyncValidator from 'async-validator'
import download from 'downloadjs'

import { i18n } from '@shared/i18n'
import { FormSchema } from '@shared/form/form-schema'
import LoadingSpinner from '@shared/components/loading-spinner.vue'

import inscriptionNewIdQuery from '@/graphql/queries/inscriptionNewId'
import inscriptionMatchingQuery from '@/graphql/queries/inscriptionMatching'

import { InscriptionModel } from '@modules/inscription/inscription-model'

export default {
  name: 'inscription-form',
  props: {
    inscription: {
      type: Object,
      default: () => ({}),
    },
    removable: {
      type: Boolean,
      default: true,
    },
    isFirst: Boolean,
    isLast: Boolean,
  },
  data() {
    return {
      isCollapsed: false,
      hasErrors: true,
      isUploadingFiles: false,
      subprogramMatchList: null,
      subprogramMatchStatus: 'pending',
      model: {
        isMember: true,
      },
      globalErrorMessage: null,
      i18n,
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      subpropertyId: 'subpropertyId',
    }),
    programId() {
      return this.$route.params.programId
    },
    fields() {
      return InscriptionModel.fields({ propertyName: 'club' })
    },
    formSchema() {
      return new FormSchema([
        this.fields.id,
        this.fields.firstName,
        this.fields.lastName,
        this.fields.birthdate,
        this.fields.gender,
        this.fields.isMember,
        this.fields.careerInfo,
        this.fields.program,
        this.fields.subprogram,
        this.fields.identDoc,
        this.fields.identDocFiles,
        this.fields.additionalDocFiles,
      ])
    },
    rules() {
      const { model, fields, formSchema } = this
      const rules = formSchema.rules()

      const careerInfoRequired = !model[fields.isMember.name]

      const additionalDocs = !!this.subprogramMatch && this.subprogramMatch.additionalDoc
      const additionalDocsRequired = additionalDocs && additionalDocs.exist

      const idDocRequired = !!this.subprogramMatch && this.subprogramMatch.identDocNeeded

      if (!careerInfoRequired) delete rules[fields.careerInfo.name]
      if (!additionalDocsRequired) delete rules[fields.additionalDocFiles.name]
      if (!idDocRequired) {
        delete rules[fields.identDoc.name]
        delete rules[fields.identDocFiles.name]
      }

      return rules
    },
    collapsedIcon() {
      if (this.hasErrors || this.subprogramMatchStatus !== 'success') return 'icon-info'
      return 'icon-player'
    },
    playerName() {
      const { firstName, lastName } = this.model

      if (firstName && lastName) return `${firstName} ${lastName}`
      else if (firstName) return firstName
      return i18n('entities.player.singular')
    },
    subprogramMatch() {
      if (!this.subprogramMatchList) return null
      if (!this.subprogramMatchList.rows) return null
      if (!this.subprogramMatchList.count) return null
      if (this.subprogramMatchStatus !== 'success') return null
      return this.subprogramMatchList.rows[0]
    },
    subprogramMatchHasExtraFields() {
      const { subprogramMatch } = this
      if (!subprogramMatch) return

      const { identDocNeeded, additionalDoc } = subprogramMatch
      const additionalDocNeeded = additionalDoc && additionalDoc.exist

      return identDocNeeded || additionalDocNeeded
    },
    isValidInscription() {
      return !this.hasErrors && this.subprogramMatchStatus === 'success'
    },
  },
  mounted() {
    this.model = {
      ...this.model,
      ...this.inscription.model,
    }
    this.syncInscription()
    this.$root.$on('inscriptions.collapse.all', this.onCollapse)
  },
  beforeDestroy() {
    this.$root.$off('inscriptions.collapse.all', this.onCollapse)
  },
  methods: {
    ...mapMutations({
      setInscriptionDeleteModalVisible: 'setInscriptionDeleteModalVisible',
      setInscriptionDeleteModalCallback: 'setInscriptionDeleteModalCallback',
    }),

    async onInscriptionClick() {
      if (!this.isCollapsed) return
      this.$root.$emit('inscriptions.collapse.all')
      await this.$nextTick()
      this.isCollapsed = false
    },

    async onCollapse() {
      this.isCollapsed = true
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return e
      }
    },

    async generateNewId() {
      return (await this.$apollo.query({
        query: inscriptionNewIdQuery,
      })).data.inscriptionNewId
    },

    onDelete() {
      const callback = () => this.$emit('deleted')
      this.setInscriptionDeleteModalCallback(callback)
      this.setInscriptionDeleteModalVisible(true)
    },

    async validate() {
      const rules = { ...this.rules }
      const validator = new AsyncValidator(rules)
      const { model } = this

      try {
        await validator.validate(model, { firstFields: true }, errors => {
          this.hasErrors = !!errors
        })
      } catch (e) {
        this.hasErrors = true
      }
    },

    async syncInscription() {
      if (this.isUploadingFiles) return
      if (!('id' in this.model)) this.model.id = await this.generateNewId()
      await this.validate()
      this.$emit('update:inscription', {
        model: this.formSchema.cast(this.model),
        match: this.subprogramMatch,
        index: this.inscription.index,
        valid: this.isValidInscription,
      })
    },

    async validateForMatch() {
      const { model, fields } = this

      const rules = {
        [fields.gender.name]: this.rules[fields.gender.name],
        [fields.birthdate.name]: this.rules[fields.birthdate.name],
      }

      const validator = new AsyncValidator(rules)

      try {
        await validator.validate(model)
        return true
      } catch (e) {
        return false
      }
    },

    async doSave() {
      try {
        await this.$refs.form.validate()
        this.isCollapsed = true
        return true
      } catch (e) {
        return false
      }
    },

    async doMatch() {
      const validForMatch = await this.validateForMatch()
      if (!validForMatch) {
        this.subprogramMatchStatus = 'pending'
        this.subprogramMatchList = null
        return
      }

      this.model = {
        ...this.model,
        [this.fields.identDoc.name]: null,
        [this.fields.identDocFiles.name]: null,
        [this.fields.additionalDocFiles.name]: null,
      };

      await this.syncInscription()

      this.subprogramMatchStatus = 'loading'
      this.subprogramMatchList = null
    },

    async doDownload($event, fileIndex, { name, publicUrl }, downloadName) {
      const isUserClick = $event.screenX && $event.screenX != 0 && $event.screenY && $event.screenY != 0

      if (isUserClick) $event.preventDefault()
      else return

      try {
        const response = await fetch(publicUrl)

        if (response.status !== 200) throw new Error('Could not fetch file')

        const blob = await response.blob()
        const filename = `${downloadName}.${name.split('.').pop()}`

        download(blob, filename, 'application/octet-stream')
      } catch (e) {
        const linkElement = this.$refs[`downloadLink--${fileIndex}`][0]
        linkElement.click()
      }
    },

    async uploadFiles() {
      const uploadFiles = []
      if ('identDocFiles' in this.$refs) uploadFiles.push(this.$refs.identDocFiles.upload())
      if ('additionalDocFiles' in this.$refs) uploadFiles.push(this.$refs.additionalDocFiles.upload())
      
      this.isUploadingFiles = true
      await Promise.all(uploadFiles)
      this.isUploadingFiles = false

      this.syncInscription()
    }
  },
  apollo: {
    subprogramMatchList: {
      query: inscriptionMatchingQuery,
      variables() {
        const { gender, birthdate } = this.formSchema.cast(this.model)
        return {
          inscription: { gender, birthdate },
          subpropertyId: this.model.subproperty,
          programId: this.model.program,
        }
      },
      update(data) {
        return data.inscriptionMatchByProgram
      },
      result() {
        if (!this.subprogramMatchList.count) this.subprogramMatchStatus = 'error'
        else this.subprogramMatchStatus = 'success'
        this.syncInscription()
      },
      skip() {
        return this.subprogramMatchStatus !== 'loading'
      },
      error() {
        this.subprogramMatchStatus = 'error'
      }
    },
  },
  watch: {
    model: {
      handler: 'syncInscription',
      deep: true,
    },
    hasErrors: 'syncInscription',
    onFormFulfilled(val) {
      if (!val) return

    },
    subpropertyId: {
      handler(id) {
        this.model.subproperty = id
      },
      immediate: true,
    },
    programId: {
      handler(id) {
        this.model.program = id
      },
      immediate: true,
    },
    subprogramMatch: {
      handler(subprogram) {
        if (!subprogram) return
        else this.model.subprogram = subprogram.id
        this.syncInscription()
      },
    }
  },
  components: {
    LoadingSpinner
  },
}
</script>

<style lang="scss" scoped>
.inscription-form {
  padding: 0 0 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);

  @include mobile {
    padding: 0 0 16px;
  }

  @include tablet {
    padding: 0 0 16px;
  }

  &__is-member {
    @include mobile {
      margin-left: 10px;
    }

    span {
      @include tablet {
        font-size: 12px;
      }
    }
  }
  
  label[for="identDocFiles"] {
    @include mobile {
      display: none;
    }

    @include tablet {
      height: 30px;
    }
  }

  label[for="additionalDocFiles"] {
    @include mobile {
      display: none;
    } 
  }

  &__buttons {
    margin-top: 8px;
  }

  &__matching {
    margin: 8px 8px 16px;
    font-size: 14px;
    height: 20px;
    display: grid;
    grid-template-columns: auto 1fr;
    grid-gap: 10px;
    align-content: center;

    &--pending,
    &--loading,
    &--success {
      color: #c5c5c5;
    }

    &--success {
      display: block;
    }

    &--error {
      color: #EE7266;
    }
  }

  &__specific {
    body &__additional-docs {
      margin: 24px 0 0;

      &__text {
        font-size: 14px;
        font-weight: bold;
        letter-spacing: -0.3px;
        color: #404040;
        margin: 0 0 10px;
      }

      &__label {
        font-size: 12px;
        font-weight: normal;
        letter-spacing: -0.2px;
        color: #c5c5c5;
        margin-left: -16px;
      }
    }

    &__additional-docs-upload {
      margin-bottom: 16px;
    }

    &__required-docs {
      &__link {
        display: grid;
        grid-template-columns: auto auto 1fr;
        align-items: center;
        align-content: center;
        justify-items: center;
        grid-gap: 24px;
        margin: 0 0 5px;
      }

      &__title {
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.3px;
        color: #404040;
      }

      body &__button {
        padding: 0 16px;
        height: 32px;
        
        font-size: 14px;
        font-weight: normal;
        letter-spacing: -0.3px;
        color: #404040;
        text-transform: none;

        &__content {
          display: grid;
          grid-template-columns: 18px 1fr;
          align-items: center;
          justify-items: center;
          grid-gap: 2px;

          &__icon {
            font-size: 20px;
            color: #2acccc;
            margin-right: 8px;
            align-self: start;
          }
        }
      }
    }
  }

  &--collapsed {
    padding: 0;
    border-bottom: 1px solid transparent;
  }

  &--last {
    border-bottom: 1px solid transparent;

    .inscription-form__state--collapsed {
      border-top: 1px solid transparent;
      border-bottom: 1px solid transparent;
    }
  }

  &--first {
    .inscription-form__state--collapsed {
      border-top: 1px solid rgba(0, 0, 0, 0.08);
    }

    .inscription-form__state--uncollapsed__header {
      border-top: 1px solid rgba(0, 0, 0, 0.08);
    }
  }

  &__state {
    .inscription-form &--error {
      color: #ee7266;
    }

    &--collapsed {
      width: 100%;
      height: 60px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      padding: 0 24px;
      box-sizing: border-box;

      display: grid;
      grid-template-columns: 20px 1fr auto;
      grid-gap: 10px;
      align-items: center;

      font-weight: normal;
      letter-spacing: -0.3px;
      color: #404040;

      cursor: pointer;

      &__label span {
        font-size: 15px;
      }

      &__icon {
        font-size: 18px;
      }

      &__delete {
        font-size: 16px;
        color: #404040;
        cursor: pointer;
      }
    }

    &--uncollapsed {
      &__header {
        padding: 16px 24px;
        background-color: #f8f8f8;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        
        @include mobile {
          padding: 16px 24px;
        }

        @include tablet {
          padding: 16px 24px;
        }

        font-size: 18px;
        font-weight: normal;
        letter-spacing: -0.4px;
        color: #2acccc;

        .el-col {
          display: grid;
          grid-template-columns: auto 1fr auto;
          grid-gap: 16px;
          align-items: center;
        }

        &__icon {
          font-size: 16px;
          cursor: pointer;
        }
      }

      &__content {
        padding: 16px 32px 0;

        @include mobile {
          padding: 8px 8px 0;
        }

        @include tablet {
          padding: 8px 16px 0;
        }
      }
    }
  }
}
</style>
